import React,{useEffect,useMemo,useState} from 'react';
import {AnimatePresence,motion,useReducedMotion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import AddFolderModal from '../Modals/AddFolderModal';
import {useTheme} from '../../contexts/ThemeContext';
import {useBookmarks} from '../../contexts/BookmarkContext';
import {useSettings} from '../../contexts/SettingsContext';
import {useAuth} from '../../contexts/AuthContext';
import {getBookmarkUsage,rankFrequentlyUsed,recordBookmarkOpen} from '../../utils/usage.js';

const {
  FiX,FiArrowLeft,FiSearch,FiFolder,FiChevronRight,FiHome,FiStar,FiActivity,FiClock,FiZap,FiFolderPlus,FiExternalLink,FiLayers
}=FiIcons;

const normaliseParent=(value)=> value || null;
const isTabletTouchViewport=()=> typeof window!=='undefined' && window.innerWidth>=768 && window.innerWidth<1024 && window.innerHeight>=600;
const NAV_EASE=[0.16,1,0.3,1];

const MobileLibraryBrowser=({onClose})=> {
  const [folderQuery,setFolderQuery]=useState('');
  const [currentParentId,setCurrentParentId]=useState(null);
  const [showAddFolder,setShowAddFolder]=useState(false);
  const [usage,setUsage]=useState({});
  const [recentFolderIds,setRecentFolderIds]=useState([]);
  const [isTabletLayout,setIsTabletLayout]=useState(isTabletTouchViewport);
  const [navDirection,setNavDirection]=useState(1);
  const reduceMotion=useReducedMotion();
  const {theme}=useTheme();
  const {settings}=useSettings();
  const {user}=useAuth();
  const {
    bookmarks,folders,currentFolder,setCurrentFolder,getBookmarkCount,getDescendantFolders,search
  }=useBookmarks();

  const recentStorageKey=user?.id ? `bmp:recent-folders:${user.id}` : null;

  useEffect(()=> {
    const handleResize=()=> setIsTabletLayout(isTabletTouchViewport());
    window.addEventListener('resize',handleResize);
    return ()=> window.removeEventListener('resize',handleResize);
  },[]);

  useEffect(()=> {
    let cancelled=false;
    if (!user?.id) {setUsage({});return undefined;}
    getBookmarkUsage(user.id).then(value=> {if (!cancelled) setUsage(value);});
    const handleUsage=(event)=> {
      if (event.detail?.userId!==user.id) return;
      setUsage(event.detail?.usage || {});
    };
    window.addEventListener('bmp:bookmark-usage',handleUsage);
    return ()=> {cancelled=true;window.removeEventListener('bmp:bookmark-usage',handleUsage);};
  },[user?.id]);

  useEffect(()=> {
    if (!recentStorageKey) {setRecentFolderIds([]);return;}
    try {
      const parsed=JSON.parse(localStorage.getItem(recentStorageKey) || '[]');
      setRecentFolderIds(Array.isArray(parsed) ? parsed.slice(0,6) : []);
    } catch {setRecentFolderIds([]);}
  },[recentStorageKey]);

  const rememberFolder=(folderId)=> {
    if (!folderId || !recentStorageKey) return;
    setRecentFolderIds(previous=> {
      const next=[folderId,...previous.filter(id=> id!==folderId)].slice(0,6);
      try {localStorage.setItem(recentStorageKey,JSON.stringify(next));} catch {}
      return next;
    });
  };

  const folderMap=useMemo(()=> new Map((folders || []).map(folder=> [folder.id,folder])),[folders]);

  const folderPath=(folderId)=> {
    const names=[];
    const seen=new Set();
    let current=folderMap.get(folderId);
    while (current && !seen.has(current.id)) {
      seen.add(current.id);
      names.unshift(current.name || 'Folder');
      current=current.parent ? folderMap.get(current.parent) : null;
    }
    return names.join(' / ');
  };

  const treeCount=(folderId)=> {
    const ids=new Set([folderId,...getDescendantFolders(folderId)]);
    return (bookmarks || []).filter(bookmark=> ids.has(bookmark.folderId)).length;
  };

  const currentParent=currentParentId ? folderMap.get(currentParentId) : null;
  const childFolders=useMemo(()=> (folders || [])
    .filter(folder=> normaliseParent(folder.parent)===normaliseParent(currentParentId))
    .sort((a,b)=> (Number(a.sortOrder) || 0)-(Number(b.sortOrder) || 0) || String(a.name || '').localeCompare(String(b.name || ''))),[folders,currentParentId]);

  const searchResults=useMemo(()=> {
    const query=folderQuery.trim().toLowerCase();
    if (!query) return [];
    return (folders || [])
      .filter(folder=> `${folder.name || ''} ${folderPath(folder.id)}`.toLowerCase().includes(query))
      .sort((a,b)=> String(a.name || '').localeCompare(String(b.name || '')))
      .slice(0,30);
  },[folderQuery,folders,folderMap]);

  const recentFolders=useMemo(()=> recentFolderIds.map(id=> folderMap.get(id)).filter(Boolean),[recentFolderIds,folderMap]);
  const frequentBookmarks=useMemo(()=> rankFrequentlyUsed(bookmarks,usage,6),[bookmarks,usage]);

  const closeAfterSelection=()=> {
    if (!isTabletLayout) onClose?.();
  };

  const selectLibraryView=(id)=> {
    search('');
    setCurrentFolder(id);
    closeAfterSelection();
  };

  const openFolder=(folder)=> {
    if (!folder) return;
    rememberFolder(folder.id);
    search('');
    setCurrentFolder(folder.id);
    const hasChildren=(folders || []).some(item=> item.parent===folder.id);
    if (hasChildren) {
      setNavDirection(1);
      setCurrentParentId(folder.id);
      setFolderQuery('');
      return;
    }
    closeAfterSelection();
  };

  const goBack=()=> {
    setNavDirection(-1);
    if (!currentParent) {setCurrentParentId(null);return;}
    setCurrentParentId(currentParent.parent || null);
  };

  const viewCurrentFolder=()=> {
    if (!currentParent) return;
    rememberFolder(currentParent.id);
    search('');
    setCurrentFolder(currentParent.id);
    closeAfterSelection();
  };

  const openFrequentBookmark=(bookmark)=> {
    if (user?.id) void recordBookmarkOpen(user.id,bookmark.id);
    window.open(bookmark.url,'_blank','noopener,noreferrer');
  };

  const handleFolderQueryChange=(event)=> {
    const value=event.target.value;
    if (!folderQuery.trim() && value.trim()) setNavDirection(1);
    else if (folderQuery.trim() && !value.trim()) setNavDirection(-1);
    setFolderQuery(value);
  };

  const rowClass=`w-full min-h-14 px-3.5 py-3 rounded-2xl flex items-center gap-3 text-left transition-colors duration-150 ${theme==='dark' ? 'active:bg-zinc-800' : 'active:bg-zinc-100'}`;
  const muted=theme==='dark' ? 'text-zinc-400' : 'text-zinc-500';
  const panelCard=theme==='dark' ? 'bg-zinc-900/70 border-zinc-800' : 'bg-zinc-50 border-zinc-100';
  const contentKey=folderQuery.trim() ? 'search' : currentParentId || 'root';
  const contentTransition=reduceMotion ? {duration:0.01} : {duration:0.2,ease:NAV_EASE};
  const contentInitial=reduceMotion ? {opacity:1,x:0} : {opacity:0,x:navDirection*22};
  const contentExit=reduceMotion ? {opacity:1,x:0} : {opacity:0,x:navDirection*-14};

  const renderFolderRow=(folder,{showPath=false}={})=> {
    const hasChildren=(folders || []).some(item=> item.parent===folder.id);
    const count=treeCount(folder.id);
    const active=currentFolder===folder.id;
    return (
      <button key={folder.id} onClick={()=> openFolder(folder)} className={`${rowClass} ${active ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-500/20' : ''}`}>
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${active ? 'bg-white/15' : 'bg-indigo-500/10 text-indigo-500'}`}><SafeIcon icon={FiFolder} className="w-5 h-5" /></div>
        <div className="flex-1 min-w-0">
          <div className="font-black text-sm truncate">{folder.name}</div>
          <div className={`text-xs mt-0.5 truncate ${active ? 'text-white/70' : muted}`}>{showPath ? folderPath(folder.id) : `${count} bookmark${count===1 ? '' : 's'}`}</div>
        </div>
        {showPath && <span className={`text-[11px] font-bold ${active ? 'text-white/70' : muted}`}>{count}</span>}
        {hasChildren && <SafeIcon icon={FiChevronRight} className={`w-5 h-5 flex-shrink-0 ${active ? 'text-white/80' : 'text-zinc-400'}`} />}
      </button>
    );
  };

  return (
    <div className={`h-full min-h-0 flex flex-col ${theme==='dark' ? 'bg-slate-900 text-white' : 'bg-white text-zinc-900'}`}>
      <div className="flex-shrink-0 px-4 pt-4 pb-3 border-b border-zinc-200/80 dark:border-zinc-800">
        <div className="flex items-center gap-3">
          {currentParentId ? (
            <button onClick={goBack} className="w-11 h-11 rounded-xl flex items-center justify-center bg-zinc-100 dark:bg-zinc-800" aria-label="Back to parent folder"><SafeIcon icon={FiArrowLeft} className="w-5 h-5" /></button>
          ) : (
            <div className="w-11 h-11 rounded-xl bg-indigo-600 text-white flex items-center justify-center"><SafeIcon icon={FiLayers} className="w-5 h-5" /></div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.24em] font-black text-indigo-500">Library</div>
            <AnimatePresence initial={false} mode="wait">
              <motion.h2 key={currentParent?.id || 'root'} initial={{opacity:0,x:reduceMotion ? 0 : navDirection*10}} animate={{opacity:1,x:0}} exit={{opacity:0,x:reduceMotion ? 0 : navDirection*-8}} transition={reduceMotion ? {duration:0.01} : {duration:0.16,ease:NAV_EASE}} className="font-black text-lg truncate">{currentParent?.name || 'Browse folders'}</motion.h2>
            </AnimatePresence>
          </div>
          <button onClick={()=> setShowAddFolder(true)} className="w-11 h-11 rounded-xl bg-indigo-600 text-white flex items-center justify-center" aria-label="Create folder"><SafeIcon icon={FiFolderPlus} className="w-5 h-5" /></button>
          <button onClick={onClose} className="w-11 h-11 rounded-xl flex items-center justify-center bg-zinc-100 dark:bg-zinc-800" aria-label="Close library"><SafeIcon icon={FiX} className="w-5 h-5" /></button>
        </div>

        <label className="mt-3 relative block">
          <SafeIcon icon={FiSearch} className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
          <input type="search" value={folderQuery} onChange={handleFolderQueryChange} placeholder="Search folders…" inputMode="search" enterKeyHint="search" autoComplete="off" className="w-full min-h-12 pl-10 pr-4 rounded-2xl bg-zinc-100 dark:bg-zinc-800 border border-transparent focus:border-indigo-500 focus:outline-none text-base" />
        </label>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-3 py-4 pb-[max(1.5rem,env(safe-area-inset-bottom,24px))]">
        <AnimatePresence initial={false} mode="wait">
          <motion.div key={contentKey} initial={contentInitial} animate={{opacity:1,x:0}} exit={contentExit} transition={contentTransition} className="min-h-full transform-gpu" style={{willChange:'transform, opacity'}}>
            {folderQuery.trim() ? (
              <div className="space-y-2">
                <div className="px-2 pb-1 text-[10px] uppercase tracking-[0.22em] font-black text-zinc-500">Folder results</div>
                {searchResults.length ? searchResults.map(folder=> renderFolderRow(folder,{showPath:true})) : <div className={`p-5 rounded-2xl border text-sm ${panelCard} ${muted}`}>No folders match “{folderQuery.trim()}”.</div>}
              </div>
            ) : currentParent ? (
              <div className="space-y-4">
                <button onClick={viewCurrentFolder} className="w-full min-h-16 rounded-2xl bg-indigo-600 text-white px-4 py-3 flex items-center gap-3 text-left shadow-lg shadow-indigo-500/20">
                  <div className="w-11 h-11 rounded-xl bg-white/15 flex items-center justify-center"><SafeIcon icon={FiHome} className="w-5 h-5" /></div>
                  <div className="flex-1"><div className="font-black">View {currentParent.name}</div><div className="text-xs text-white/70 mt-0.5">{treeCount(currentParent.id)} bookmarks including subfolders</div></div>
                  <SafeIcon icon={FiChevronRight} className="w-5 h-5" />
                </button>
                <div>
                  <div className="px-2 pb-2 text-[10px] uppercase tracking-[0.22em] font-black text-zinc-500">Subfolders</div>
                  <div className="space-y-1">{childFolders.length ? childFolders.map(folder=> renderFolderRow(folder)) : <div className={`p-5 rounded-2xl border text-sm ${panelCard} ${muted}`}>No subfolders here. Use “View {currentParent.name}” to open its bookmarks.</div>}</div>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                {frequentBookmarks.length>0 && (
                  <section>
                    <div className="px-2 pb-2 flex items-center justify-between"><div className="text-[10px] uppercase tracking-[0.22em] font-black text-zinc-500">Frequently Used</div><div className="text-[10px] font-bold text-zinc-400">This device</div></div>
                    <div className="grid grid-cols-2 gap-2">
                      {frequentBookmarks.map(bookmark=> (
                        <button key={bookmark.id} onClick={()=> openFrequentBookmark(bookmark)} className={`min-h-20 rounded-2xl border p-3 text-left ${panelCard}`}>
                          <div className="flex items-center justify-between gap-2"><SafeIcon icon={FiZap} className="w-4 h-4 text-amber-500" /><SafeIcon icon={FiExternalLink} className="w-3.5 h-3.5 text-zinc-400" /></div>
                          <div className="font-black text-xs truncate mt-2">{bookmark.title}</div>
                          <div className={`text-[10px] mt-1 ${muted}`}>{bookmark._usageCount} open{bookmark._usageCount===1 ? '' : 's'}</div>
                        </button>
                      ))}
                    </div>
                  </section>
                )}

                {recentFolders.length>0 && (
                  <section>
                    <div className="px-2 pb-2 flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-black text-zinc-500"><SafeIcon icon={FiClock} className="w-3.5 h-3.5" />Recent Folders</div>
                    <div className="space-y-1">{recentFolders.slice(0,4).map(folder=> renderFolderRow(folder))}</div>
                  </section>
                )}

                <section>
                  <div className="px-2 pb-2 text-[10px] uppercase tracking-[0.22em] font-black text-zinc-500">Library Views</div>
                  <div className="space-y-1">
                    <button onClick={()=> selectLibraryView('ALL')} className={rowClass}><div className="w-11 h-11 rounded-xl bg-indigo-500/10 text-indigo-500 flex items-center justify-center"><SafeIcon icon={FiHome} className="w-5 h-5" /></div><div className="flex-1"><div className="font-black text-sm">All Bookmarks</div><div className={`text-xs mt-0.5 ${muted}`}>{getBookmarkCount('ALL')} bookmarks</div></div><SafeIcon icon={FiChevronRight} className="w-5 h-5 text-zinc-400" /></button>
                    <button onClick={()=> selectLibraryView('UNFILED')} className={rowClass}><div className="w-11 h-11 rounded-xl bg-amber-500/10 text-amber-500 flex items-center justify-center"><SafeIcon icon={FiStar} className="w-5 h-5" /></div><div className="flex-1"><div className="font-black text-sm">Unfiled</div><div className={`text-xs mt-0.5 ${muted}`}>{getBookmarkCount('UNFILED')} bookmarks</div></div><SafeIcon icon={FiChevronRight} className="w-5 h-5 text-zinc-400" /></button>
                    <button onClick={()=> selectLibraryView('HEALTH')} className={rowClass}><div className="w-11 h-11 rounded-xl bg-emerald-500/10 text-emerald-500 flex items-center justify-center"><SafeIcon icon={FiActivity} className="w-5 h-5" /></div><div className="flex-1"><div className="font-black text-sm">Library Health</div><div className={`text-xs mt-0.5 ${muted}`}>Duplicates, stale links and checks</div></div><SafeIcon icon={FiChevronRight} className="w-5 h-5 text-zinc-400" /></button>
                  </div>
                </section>

                {(settings.smartCollections || []).length>0 && (
                  <section>
                    <div className="px-2 pb-2 text-[10px] uppercase tracking-[0.22em] font-black text-zinc-500">Smart Collections</div>
                    <div className="space-y-1">{(settings.smartCollections || []).map(collection=> <button key={collection.id} onClick={()=> selectLibraryView(`SMART:${collection.id}`)} className={rowClass}><div className="w-11 h-11 rounded-xl bg-violet-500/10 text-violet-500 flex items-center justify-center"><SafeIcon icon={FiSearch} className="w-5 h-5" /></div><div className="flex-1 min-w-0"><div className="font-black text-sm truncate">{collection.name}</div><div className={`text-xs mt-0.5 truncate ${muted}`}>{collection.query || 'Saved collection'}</div></div><SafeIcon icon={FiChevronRight} className="w-5 h-5 text-zinc-400" /></button>)}</div>
                  </section>
                )}

                <section>
                  <div className="px-2 pb-2 text-[10px] uppercase tracking-[0.22em] font-black text-zinc-500">Folders</div>
                  <div className="space-y-1">{childFolders.length ? childFolders.map(folder=> renderFolderRow(folder)) : <div className={`p-5 rounded-2xl border text-sm ${panelCard} ${muted}`}>No folders yet. Create one with the + folder button above.</div>}</div>
                </section>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      <AddFolderModal isOpen={showAddFolder} onClose={()=> setShowAddFolder(false)} defaultParentId={currentParentId} />
    </div>
  );
};

export default MobileLibraryBrowser;